/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { call, delay, put, race } from "redux-saga/effects";

import { HttpStatusCode } from "Constants/HttpStatusCode";
import { CLAIMED_FROM, CLAIMED_TO } from "Constants/M365CopilotBenefitConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IM365CopilotBenefitSearchRequest } from "Interfaces/Benefits/IM365CopilotBenefitSearchRequest";
import { IM365CopilotBenefitSearchResponse } from "Interfaces/Benefits/IM365CopilotBenefitSearchResponse";
import { IApiErrorResult, IApiResult2 } from "Interfaces/IApiResult";
import {
  searchMvpM365CopilotBenefit,
  searchRdM365CopilotBenefit,
} from "Services/M365CopilotBenefitApi";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import {
  searchM365CopilotBenefitFail,
  searchM365CopilotBenefitSuccess,
  ISearchM365CopilotBenefit,
} from "./M365CopilotBenefitAction";

/**
 * Search M365 Copilot benefits
 * @param action for searching M365 Copilot benefits
 * @returns {Generator}
 */
function* handleSearchM365CopilotBenefits(action: ISearchM365CopilotBenefit): Generator {
  const defaultErrorMessage = "Failed to search M365 Copilot benefits";
  const requestBody: IM365CopilotBenefitSearchRequest = {
    searchKey: action.searchText,
    pageSize: action.pageSize,
    pageIndex: action.searchPage,
  };

  action.selectedBenefitsFilterValues.forEach((item) => {
    switch (item.fieldName) {
      case CLAIMED_FROM:
        requestBody.claimedFrom = new Date(item.value);
        break;
      case CLAIMED_TO:
        requestBody.claimedTo = new Date(item.value);
        break;
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const apiEndpoint = ((tenant: TenantTypesEnum) => {
    switch (tenant) {
      case TenantTypesEnum.MVP:
        return searchMvpM365CopilotBenefit;
      case TenantTypesEnum.RD:
        return searchRdM365CopilotBenefit;
    }
  })(action.tenant);

  if (apiEndpoint === undefined) {
    yield put(searchM365CopilotBenefitFail(defaultErrorMessage));
    return;
  }

  try {
    const { result, timeout }: IApiResult2<IM365CopilotBenefitSearchResponse> | any = yield race({
      result: call(apiEndpoint, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchM365CopilotBenefitFail(defaultErrorMessage));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const parsedBody = result.parsedBody as IM365CopilotBenefitSearchResponse;
        yield put(searchM365CopilotBenefitSuccess(parsedBody.benefits, parsedBody.filteredCount));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          searchM365CopilotBenefitFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(searchM365CopilotBenefitFail(defaultErrorMessage));
        break;
    }
  } catch (error) {
    yield put(searchM365CopilotBenefitFail(error));
  }
}

export { handleSearchM365CopilotBenefits };
